/**
 * GITO brand colors — single source of truth for UI and charts.
 * Spec: primära (mörkblå/ljusblå), sekundär (turkos), accent (energi/puls/volt/kraft), gråskala.
 *
 * Primary scale: 500 = mörkblå (#002F6C), 300 = ljusblå (#00B5E2), 50–200 = ljusa bakgrunder.
 */

const brand = {
    darkBlue: "#002F6C",
    lightBlue: "#00B5E2",
    turquoise: "#00BFB3",
    asphalt: "#F2F2F2",
    energi: "#FAE100",
    puls: "#D40F7D",
    volt: "#440099",
    kraft: "#FF9E1B",
    green: "#008A5E",
    red: "#C8102E",
    black: "#000000",
    white: "#FFFFFF",
};

function hexToRgb(hex) {
    const n = hex.replace("#", "");
    return {
        r: parseInt(n.slice(0, 2), 16),
        g: parseInt(n.slice(2, 4), 16),
        b: parseInt(n.slice(4, 6), 16),
    };
}

function rgbToHex(r, g, b) {
    return `#${[r, g, b]
        .map((v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, "0"))
        .join("")}`;
}

function mix(hexA, hexB, weightA) {
    const a = hexToRgb(hexA);
    const b = hexToRgb(hexB);
    const w = weightA / 100;
    return rgbToHex(
        a.r * w + b.r * (1 - w),
        a.g * w + b.g * (1 - w),
        a.b * w + b.b * (1 - w)
    );
}

const tint = (hex, pct) => mix(hex, "#FFFFFF", pct);
const shade = (hex, pct) => mix(hex, "#000000", pct);

function buildScale(base, options = {}) {
    return {
        50: tint(base, 10),
        100: tint(base, 18),
        200: tint(base, 30),
        300: tint(base, 45),
        400: tint(base, 65),
        500: base,
        600: shade(base, 88),
        700: shade(base, 75),
        800: options.darkBase ? options.darkBase : shade(base, 60),
        900: options.darkBase ? shade(options.darkBase, 92) : shade(base, 45),
        950: options.darkBase ? shade(options.darkBase, 85) : shade(base, 30),
    };
}

function buildPrimaryScale(darkBlue, lightBlue) {
    const blend = mix(darkBlue, lightBlue, 40);
    return {
        50: tint(lightBlue, 12),
        100: tint(lightBlue, 22),
        200: tint(lightBlue, 38),
        300: lightBlue,
        400: blend,
        500: darkBlue,
        600: shade(darkBlue, 92),
        700: shade(darkBlue, 78),
        800: shade(darkBlue, 62),
        900: shade(darkBlue, 48),
        950: shade(darkBlue, 32),
    };
}

const primaryBlend = mix(brand.darkBlue, brand.lightBlue, 40);

const tailwindColors = {
    primary: buildPrimaryScale(brand.darkBlue, brand.lightBlue),
    secondary: buildScale(brand.turquoise),
    error: buildScale(brand.red),
    puls: buildScale(brand.puls),
    warning: buildScale(brand.energi),
    energi: buildScale(brand.energi),
    success: buildScale(brand.green),
    volt: buildScale(brand.volt),
    kraft: buildScale(brand.kraft),
    mute: {
        // Page canvas — asphalt tinted toward white (between white and mute-50)
        25: tint(brand.asphalt, 45),
        50: brand.asphalt,
        100: "#E8E8E8",
        200: "#D1D1D1",
        300: "#BABABA",
        400: "#A3A3A3",
        500: "#8C8C8C",
        600: "#757575",
        700: "#5E5E5E",
        800: "#474747",
        900: "#303030",
        950: "#1A1A1A",
    },
    black: "#000000",
    white: brand.white,
};

/** ApexCharts blues — primary-400 first, primary-300 (ljusblå) as accent. */
const chartPrimary = tailwindColors.primary[400];
const chartPrimaryLight = tailwindColors.primary[300];

/** Default multi-series palette for ApexCharts (sessions, donuts, etc.). */
const chartSeriesColors = [
    chartPrimary,
    chartPrimaryLight,
    brand.turquoise,
    brand.energi,
    brand.red,
    brand.volt,
    brand.kraft,
];

function chartGradientForColor(color) {
    if (color === chartPrimary) {
        return chartPrimaryLight;
    }
    if (color === chartPrimaryLight) {
        return primaryBlend;
    }
    return tint(color, 70);
}

const chartGradientToColors = chartSeriesColors.map(chartGradientForColor);

const chartForecastActual = {
    colors: [chartPrimary, brand.kraft],
    gradientToColors: [chartPrimaryLight, tint(brand.kraft, 70)],
};

const chartAreaFill = {
    colors: [chartPrimary],
    gradientToColors: [chartPrimaryLight],
};

const chartGradientDefaults = {
    shade: "light",
    type: "vertical",
    shadeIntensity: 0.5,
    inverseColors: false,
    opacityFrom: 0.9,
    opacityTo: 0.1,
    stops: [0, 90, 100],
};

function chipStyleVarsFromAccent(accent) {
    return {
        "--st-bg": tint(accent, 25),
        "--st-text": brand.darkBlue,
        "--st-border": accent,
    };
}

const statusAccentByKey = {
    AVAILABLE: brand.green,
    PREPARING: brand.kraft,
    OCCUPIED: brand.kraft,
    CHARGING: mix(brand.darkBlue, brand.lightBlue, 45),
    EVCONNECTED: brand.kraft,
    IDLE: brand.volt,
    FINISHING: brand.lightBlue,
    INOPERATIVE: tailwindColors.mute[700],
    UNAVAILABLE: tailwindColors.mute[700],
    RESERVED: brand.volt,
    PENDING: mix(brand.kraft, brand.darkBlue, 78),
    FAULTED: brand.puls,
    SUSPENDEDEV: brand.volt,
    SUSPENDEDEVSE: brand.volt,
    ALERTING: brand.puls,
    DELTA: brand.kraft,
    PERIODIC: brand.turquoise,
    ACTIVE: brand.green,
    ACCEPTED: brand.green,
    FINISHED: brand.lightBlue,
    REJECTED: brand.puls,
    EXPIRED: tailwindColors.mute[700],
    INVALID: brand.puls,
    CONCURRENTTX: brand.kraft,
    ONLINE: brand.green,
    OFFLINE: brand.red,
    SUCCESS: brand.green,
    INFO: brand.darkBlue,
    WARNING: brand.kraft,
    ERROR: brand.puls,
    POINT: brand.volt,
    CONNECTOR: brand.turquoise,
    LIVE: brand.green,
    DISCONNECTED: brand.red,
    OTHER: tailwindColors.mute[600],
    DEFAULT: tailwindColors.mute[600],
};

const logTypeAccentByKey = {
    status: tailwindColors.mute[700],
    authorization: brand.volt,
    transaction: brand.darkBlue,
    bootnotification: brand.turquoise,
    heartbeat: brand.turquoise,
    "charging-command": brand.kraft,
    "charging command": brand.kraft,
    changerequest: shade(brand.kraft, 82),
    "change-request": shade(brand.kraft, 82),
    bookingrequest: brand.volt,
    "booking-request": brand.volt,
    chargeattempt: brand.lightBlue,
    "charge-attempt": brand.lightBlue,
    other: tailwindColors.mute[700],
};

function getStatusAccent(status = "") {
    const key = status?.toUpperCase();
    return statusAccentByKey[key] ?? statusAccentByKey.DEFAULT;
}

function getLogTypeAccent(logType = "") {
    const key = (logType ?? "").toLowerCase();
    return logTypeAccentByKey[key] ?? logTypeAccentByKey.other;
}

function getStatusChipStyleVars(status = "") {
    return chipStyleVarsFromAccent(getStatusAccent(status));
}

function getLogTypeChipStyleVars(logType = "") {
    return chipStyleVarsFromAccent(getLogTypeAccent(logType));
}

function getStatusColorStyle(status = "") {
    return { color: getStatusAccent(status) };
}

function getStatusStripeStyle(status = "") {
    return { backgroundColor: getStatusAccent(status) };
}

function getStatusHexColor(status = "") {
    return getStatusAccent(status);
}

const chartPowerDual = {
    colors: [brand.kraft, chartPrimary],
    gradientToColors: [tint(brand.kraft, 70), chartPrimaryLight],
};

/** Charge Monitor action buttons — soft fills, distinct hues, stronger borders. Text: mörkblå. */
const chargeMonitorButtons = {
    datalog: {
        bg: mix(brand.darkBlue, brand.white, 12),
        border: mix(brand.darkBlue, brand.lightBlue, 50),
        hover: mix(brand.darkBlue, brand.white, 20),
        text: brand.darkBlue,
    },
    start: {
        bg: mix(brand.lightBlue, brand.white, 45),
        border: brand.lightBlue,
        hover: mix(brand.lightBlue, brand.white, 58),
        text: brand.darkBlue,
    },
    availability: {
        bg: mix(brand.energi, brand.white, 48),
        border: mix(brand.energi, brand.kraft, 75),
        hover: mix(brand.energi, brand.white, 60),
        text: brand.darkBlue,
    },
    restart: {
        bg: mix(brand.kraft, brand.white, 42),
        border: brand.kraft,
        hover: mix(brand.kraft, brand.white, 55),
        text: brand.darkBlue,
    },
    unlock: {
        bg: brand.asphalt,
        border: tailwindColors.mute[400],
        hover: mix(brand.asphalt, brand.darkBlue, 92),
        text: brand.darkBlue,
    },
    details: {
        bg: mix(mix(brand.turquoise, brand.lightBlue, 45), brand.white, 32),
        border: mix(brand.lightBlue, brand.darkBlue, 55),
        hover: mix(mix(brand.turquoise, brand.lightBlue, 45), brand.white, 45),
        text: brand.darkBlue,
    },
    stop: {
        bg: mix(brand.red, brand.white, 38),
        border: brand.red,
        hover: mix(brand.red, brand.white, 50),
        text: brand.darkBlue,
    },
};

function getChargeMonitorButtonStyle(key = "") {
    const colors = chargeMonitorButtons[key] ?? chargeMonitorButtons.unlock;
    return {
        "--cm-btn-bg": colors.bg,
        "--cm-btn-hover": colors.hover,
        "--cm-btn-border": colors.border,
        "--cm-btn-text": colors.text,
    };
}

module.exports = {
    brand,
    primaryBlend,
    tailwindColors,
    chartPrimary,
    chartPrimaryLight,
    chartSeriesColors,
    chartGradientToColors,
    chartForecastActual,
    chartAreaFill,
    chartPowerDual,
    chartGradientDefaults,
    chargeMonitorButtons,
    getChargeMonitorButtonStyle,
    statusAccentByKey,
    statusHexByKey: statusAccentByKey,
    logTypeAccentByKey,
    getStatusAccent,
    getLogTypeAccent,
    getStatusChipStyleVars,
    getLogTypeChipStyleVars,
    chipStyleVarsFromAccent,
    getStatusColorStyle,
    getStatusStripeStyle,
    getStatusHexColor,
};
